import {
  CircleCheckBig,
  CircleSlash,
  Clock4,
  Hourglass,
  PencilLine,
  Rocket,
  type LucideIcon,
} from 'lucide-react';
import { describeVersionState, versionBucket, type VersionBucket } from '@asm/shared';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';

export type VersionStatusKey = VersionBucket | 'live';

export const VERSION_STATUS_META: Record<VersionStatusKey, {
  label: string;
  icon: LucideIcon;
  tone: string;
  panel: string;
  badge: string;
}> = {
  rejected: {
    label: 'Rejected',
    icon: CircleSlash,
    tone: 'text-rose-700 dark:text-rose-300',
    panel: 'border-rose-500/40 bg-rose-500/10',
    badge: 'border border-rose-500/30 bg-rose-500/15 text-rose-700 dark:text-rose-300',
  },
  waiting: {
    label: 'Waiting for Review',
    icon: Clock4,
    tone: 'text-sky-700 dark:text-sky-300',
    panel: 'border-sky-500/40 bg-sky-500/10',
    badge: 'border border-sky-500/30 bg-sky-500/15 text-sky-700 dark:text-sky-300',
  },
  inReview: {
    label: 'In Review',
    icon: Hourglass,
    tone: 'text-amber-700 dark:text-amber-300',
    panel: 'border-amber-500/40 bg-amber-500/10',
    badge: 'border border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300',
  },
  approved: {
    label: 'Approved',
    icon: CircleCheckBig,
    tone: 'text-violet-700 dark:text-violet-300',
    panel: 'border-violet-500/40 bg-violet-500/10',
    badge: 'border border-violet-500/30 bg-violet-500/15 text-violet-700 dark:text-violet-300',
  },
  live: {
    label: 'Live',
    icon: Rocket,
    tone: 'text-emerald-700 dark:text-emerald-300',
    panel: 'border-emerald-500/40 bg-emerald-500/10',
    badge: 'border border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  },
  draft: {
    label: 'Draft',
    icon: PencilLine,
    tone: 'text-slate-600 dark:text-slate-300',
    panel: 'border-slate-400/35 bg-slate-500/8',
    badge: 'border border-slate-400/30 bg-slate-500/10 text-slate-600 dark:text-slate-300',
  },
};

const LIVE_STATES = new Set(['READY_FOR_SALE', 'READY_FOR_DISTRIBUTION', 'PREORDER_READY_FOR_SALE', 'PUBLISHED', 'ACTIVE', 'ENABLED']);
const REJECTED_STATES = new Set(['UNRESOLVED_ISSUES', 'FAILED', 'ERROR', 'REMOVED_FROM_SALE', 'DEVELOPER_REMOVED_FROM_SALE']);
const WAITING_STATES = new Set(['SUBMITTED', 'PENDING']);
const REVIEW_STATES = new Set(['PROCESSING', 'IN_PROGRESS']);
const APPROVED_STATES = new Set(['APPROVED', 'COMPLETE', 'COMPLETED', 'VALID']);

export function versionStatusKey(state?: string | null, explicit?: VersionStatusKey): VersionStatusKey {
  if (explicit) return explicit;
  if (!state) return 'draft';
  if (LIVE_STATES.has(state)) return 'live';
  if (REJECTED_STATES.has(state)) return 'rejected';
  if (WAITING_STATES.has(state)) return 'waiting';
  if (REVIEW_STATES.has(state)) return 'inReview';
  if (APPROVED_STATES.has(state)) return 'approved';
  return versionBucket(state) ?? 'draft';
}

export function VersionStatusBadge({
  state,
  versionString,
  kind,
  label,
  className,
}: {
  state?: string | null;
  versionString?: string | null;
  kind?: VersionStatusKey;
  label?: string;
  className?: string;
}) {
  const key = versionStatusKey(state, kind);
  const meta = VERSION_STATUS_META[key];
  const Icon = meta.icon;
  const text = label ?? (state ? describeVersionState(state) : meta.label);
  return (
    <Badge className={cn(meta.badge, className)}>
      <Icon className={cn('size-3', key === 'inReview' && 'animate-pulse')} />
      {versionString ? `v${versionString} · ` : ''}{text}
    </Badge>
  );
}
